<template>
  <div class="language-switcher">
    <div class="language-dropdown">
      <button
        @click="toggleDropdown"
        class="language-icon-btn"
        :title="getCurrentLanguageName()"
      >
        <span class="globe-icon">🌐</span>
        <span class="language-text">{{ getCurrentLanguageCode() }}</span>
        <span class="dropdown-arrow">▼</span>
      </button>
      
      <div v-if="showDropdown" class="language-menu">
        <button
          v-for="lang in languages"
          :key="lang.code"
          @click="switchLanguage(lang.code)"
          class="language-option"
          :class="{ active: currentLanguage === lang.code }"
        >
          <span class="lang-flag">{{ lang.flag }}</span>
          <span class="lang-name">{{ lang.name }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

// const instance = getCurrentInstance()
const { locale } = useI18n()
const route = useRoute()
const router = useRouter()

// 组件导入 i18n
import { i18n } from '../main'

const showDropdown = ref(false)

// 支持的语言列表
const languages = [
  { code: 'zh', name: '中文', flag: '🇨🇳', locale: 'zh-CN' },
  // { code: 'zh-TW', name: '繁体中文', flag: '🇹🇼', locale: 'zh-TW' },
  { code: 'en', name: 'English', flag: '🇺🇸', locale: 'en-US' }   //,
  // { code: 'ko', name: '한국어', flag: '🇰🇷', locale: 'ko-KR' },
  // { code: 'ja', name: '日本語', flag: '🇯🇵', locale: 'ja-JP' },
  // { code: 'fr', name: 'Français', flag: '🇫🇷', locale: 'fr-FR' },
  // { code: 'de', name: 'Deutsch', flag: '🇩🇪', locale: 'de-DE' }
]

const currentLanguage = computed(() => {
  // 从路由参数中获取语言
  const lang = route.params.lang as string
  return lang || 'zh'
})

// 获取当前语言名称
const getCurrentLanguageName = () => {
  const lang = languages.find(l => l.code === currentLanguage.value)
  return lang ? lang.name : '中文'
}

// 获取当前语言代码
const getCurrentLanguageCode = () => {
  const lang = languages.find(l => l.code === currentLanguage.value)
  return lang ? lang.code.toUpperCase() : 'ZH'
}

// 监听路由语言参数变化，同步更新 i18n locale
watch(() => route.params.lang, async (newLang) => {
  if (newLang) {
    const lang = languages.find(l => l.code === newLang)
    if (lang) {
      // 先加载语言包（如果未加载）
      if (i18n.global.availableLocales.indexOf(lang.locale) === -1) {
        try {
          let messages
          
          // 根据环境选择加载方式
          if (import.meta.env.DEV) {
            // 开发环境：使用动态 import 加载 TS 文件
            const module = await import(`../locales/${lang.locale}.ts`)
            messages = module.default
            // console.log(`[开发环境] 语言包加载并设置成功：${lang.locale}`)
          } else {
            // 生产环境：使用 fetch 加载 JSON 文件
            const response = await fetch(`/locales/${lang.locale}.json`)
            if (!response.ok) {
              throw new Error(`Failed to load locale file: ${lang.locale}`)
            }
            messages = await response.json()
            // console.log(`[生产环境] 语言包加载并设置成功：${lang.locale}`)
          }
          
          i18n.global.setLocaleMessage(lang.locale, messages)
        } catch (error) {
          console.error(`加载语言包失败 ${lang.locale}:`, error)
        }
      }
      
      // 设置当前语言
      locale.value = lang.locale
      localStorage.setItem('language', lang.locale)
      document.documentElement.lang = lang.locale
      document.documentElement.setAttribute('lang', lang.locale)
      // console.log('路由语言变化，同步更新 i18n locale:', lang.locale)
    }
  }
}, { immediate: true })

// 切换下拉菜单
const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value
}

// 切换语言
const switchLanguage = async (langCode: string) => {
  const lang = languages.find(l => l.code === langCode)
  if (!lang) return
  
  // 先加载语言包（如果未加载）
  if (i18n.global.availableLocales.indexOf(lang.locale) === -1) {
    try {
      let messages
      
      // 根据环境选择加载方式
      if (import.meta.env.DEV) {
        // 开发环境：使用动态 import 加载 TS 文件
        const module = await import(`../locales/${lang.locale}.ts`)
        messages = module.default
        // console.log(`[开发环境] 语言包预加载成功：${lang.locale}`)
      } else {
        // 生产环境：使用 fetch 加载 JSON 文件
        const response = await fetch(`/locales/${lang.locale}.json`)
        if (!response.ok) {
          throw new Error(`Failed to load locale file: ${lang.locale}`)
        }
        messages = await response.json()
        // console.log(`[生产环境] 语言包预加载成功：${lang.locale}`)
      }
      
      i18n.global.setLocaleMessage(lang.locale, messages)
    } catch (error) {
      console.error(`加载语言包失败 ${lang.locale}:`, error)
    }
  }
  
  // 更新 i18n locale
  locale.value = lang.locale
  // console.log('i18n locale updated to:', locale.value)
  
  // 保存到 localStorage
  localStorage.setItem('language', lang.locale)
  
  // 更新 HTML lang 属性
  document.documentElement.lang = lang.locale
  document.documentElement.setAttribute('lang', lang.locale)
  
  // 获取当前路径并替换语言部分
  const currentPath = route.path
  // 使用更通用的正则表达式，匹配所有可能的语言代码（包括 zh-TW 这种带连字符的）
  const pathWithoutLang = currentPath.replace(/^\/([a-z]{2}(?:-[A-Z]{2})?)(\/|$)/, '$2')
  const newPath = `/${langCode}${pathWithoutLang}`
  
  // 路由跳转
  router.push(newPath)
  
  // 关闭下拉菜单
  showDropdown.value = false
  
  // console.log(`语言已切换到：${lang.name}`, 'locale:', locale.value, 'path:', newPath)
  
  // 刷新页面以确保所有 fetch 请求都使用新的语言参数
  setTimeout(() => {
    window.location.reload()
  }, 300)
}

// 点击外部关闭下拉菜单
const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement
  if (!target.closest('.language-dropdown')) {
    showDropdown.value = false
  }
}

// 初始化语言设置（不再从 localStorage 恢复，以路由参数为准）
const initLanguage = () => {
  // 路由参数优先，如果没有路由参数则使用 localStorage
  const routeLang = route.params.lang as string
  if (routeLang) {
    const lang = languages.find(l => l.code === routeLang)
    if (lang) {
      locale.value = lang.locale
      document.documentElement.lang = lang.locale
      localStorage.setItem('language', lang.locale)
      // console.log('根据路由参数初始化语言:', lang.locale)
      return
    }
  }
  
  // 如果没有路由参数，使用 localStorage
  const savedLang = localStorage.getItem('language')
  if (savedLang) {
    const lang = languages.find(l => l.locale === savedLang || l.code === savedLang)
    if (lang) {
      locale.value = lang.locale
      document.documentElement.lang = lang.locale
      // console.log('根据 localStorage 初始化语言:', lang.locale)
    }
  }
}

// 组件挂载时初始化语言和事件监听
onMounted(() => {
  initLanguage()
  document.addEventListener('click', handleClickOutside)
})

// 组件卸载时清理事件监听
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.language-switcher {
  display: inline-block;
  position: relative;
}

.language-dropdown {
  position: relative;
}

.language-icon-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #374151;
  font-size: 14px;
}

.globe-icon {
  font-size: 18px;
}

.language-text {
  font-weight: 500;
  min-width: 25px;
}

.dropdown-arrow {
  font-size: 10px;
  transition: transform 0.3s ease;
}

.language-icon-btn:hover {
  background-color: #f3f4f6;
}

.language-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 50;
  min-width: 150px;
  overflow: hidden;
  margin-top: 4px;
}

.language-option {
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  transition: background-color 0.2s ease;
}

.language-option:hover {
  background-color: #f3f4f6;
}

.language-option.active {
  background-color: #eff6ff;
  color: #2563eb;
}

.lang-flag {
  font-size: 16px;
}

.lang-name {
  font-weight: 500;
}

/* 响应式设计 */
@media (max-width: 640px) {
  .language-icon-btn {
    padding: 6px 8px;
    font-size: 13px;
  }
  
  .globe-icon {
    font-size: 16px;
  }
  
  .language-text {
    font-size: 12px;
  }
  
  .language-menu {
    min-width: 130px;
  }
  
  .language-option {
    padding: 8px 10px;
    font-size: 13px;
  }
}
</style>
